import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import PlayerHeadshot from "../components/PlayerHeadshot";
import DebateThread from "../components/community/DebateThread";
import {
  useGetCommunityDebateQuery,
  useVoteCommunityDebateMutation,
  useCommentCommunityDebateMutation,
} from "../store/api/communityApi";
import { useAuth } from "../contexts/AuthContext";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx, brandOutlinedButtonSx } from "../utils/brandAssets";
import {
  communityCreateDebatePath,
  isOfficialDailyDebate,
  isVotingClosed,
} from "../utils/community";
import { getPlayerPath as playerPath } from "../utils/fanPage";
import { colors } from "../theme";

const Candidate = ({
  candidate,
  sport,
  selected,
  percent,
  votes,
  onVote,
  disabled,
}) => (
  <Box
    sx={{
      border: `1px solid ${selected ? "rgba(132, 230, 229, 0.5)" : colors.border}`,
      borderRadius: 2,
      p: 2,
    }}
  >
    <PlayerHeadshot playerId={candidate?.playerId} sport={sport} size={88} />
    <Typography
      variant="h5"
      fontWeight={800}
      component={RouterLink}
      to={playerPath(candidate?.playerId, sport)}
      sx={{ color: "inherit", textDecoration: "none" }}
    >
      {candidate?.name}
    </Typography>
    {percent != null && (
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
        {percent}% · {votes} {votes === 1 ? "vote" : "votes"}
      </Typography>
    )}
    <Button
      fullWidth
      variant={selected ? "contained" : "outlined"}
      disabled={disabled}
      sx={{ mt: 2, ...(selected ? brandFilledButtonSx : brandOutlinedButtonSx) }}
      onClick={onVote}
    >
      {selected ? `Voted ${candidate?.name}` : `Vote ${candidate?.name}`}
    </Button>
  </Box>
);

const CommunityDebate = ({ publicView = false }) => {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: debate, isLoading, error } = useGetCommunityDebateQuery(idOrSlug, {
    skip: !idOrSlug,
  });
  const [vote, { isLoading: voting }] = useVoteCommunityDebateMutation();
  const [comment] = useCommentCommunityDebateMutation();
  const { trackCustomEvent } = useAnalytics();
  const [notice, setNotice] = React.useState("");

  const handleVote = async (choiceId) => {
    if (isVotingClosed(debate)) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      await vote({ id: debate.id, choiceId }).unwrap();
      trackCustomEvent("debate_voted", { debate_id: debate.id });
    } catch (err) {
      setNotice(err?.data?.error || "Could not record your vote");
    }
  };

  const handleComment = async (body, parentId) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      await comment({ id: debate.id, body, parentId }).unwrap();
    } catch (err) {
      setNotice(err?.data?.error || "Could not post your comment");
      throw err;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !debate) {
    return <Alert severity="info">Debate not found.</Alert>;
  }

  const official = isOfficialDailyDebate(debate);
  const gated = official && debate.voteCounts == null;
  const votingClosed = isVotingClosed(debate);
  const candidates = debate.candidates || [];
  const fourUp = official && candidates.length === 4;
  const percents = gated
    ? candidates.map(() => null)
    : debate.percents ||
      (debate.voteCounts || []).map((count) => {
        const total = debate.totalVotes || 0;
        return total ? Math.round((count / total) * 100) : 0;
      });
  const total = debate.totalVotes || 0;

  return (
    <Box sx={{ py: publicView ? 0 : { xs: 1, sm: 2 }, maxWidth: 880, mx: "auto" }}>
      <SeoHead
        title={`${debate.question} — Stats.Fan Community`}
        description={
          gated
            ? debate.question
            : `${candidates.map((candidate) => candidate.name).join(" vs ")}. ${total} community votes.`
        }
      />
      {debate.official && (
        <Typography variant="overline" color="text.secondary">
          Stats.fan Official
        </Typography>
      )}
      <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
        {debate.question}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: fourUp
            ? { xs: "1fr 1fr", md: "1fr 1fr" }
            : { xs: "1fr", md: "1fr auto 1fr" },
          gap: 2,
          alignItems: "start",
          mt: 4,
        }}
      >
        {candidates.map((candidate, index) => (
          <React.Fragment key={candidate.playerId}>
            {!fourUp && index === 1 && (
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ alignSelf: "center", textAlign: "center" }}
              >
                VS
              </Typography>
            )}
            <Candidate
              candidate={candidate}
              sport={debate.sport}
              selected={debate.viewerChoiceId === candidate.playerId}
              percent={percents[index]}
              votes={gated ? 0 : debate.voteCounts?.[index] || 0}
              disabled={votingClosed || voting}
              onVote={() => handleVote(candidate.playerId)}
            />
          </React.Fragment>
        ))}
      </Box>
      {Array.isArray(debate.comments) && (
        <DebateThread
          comments={debate.comments}
          canComment={isAuthenticated}
          onComment={handleComment}
        />
      )}
      <Button
        component={RouterLink}
        to={communityCreateDebatePath(debate.sport)}
        sx={{ ...brandOutlinedButtonSx, mt: 4 }}
      >
        Start another debate
      </Button>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default CommunityDebate;
